import { Flex, Space, Tooltip } from 'antd'
import getTeeth from '../../../helpers/fields-calculation/stamps/get-teeth'
import getMaxWidthOfMaterial from '../../../helpers/fields-calculation/stamps/get-max-width-of-material'
import getNumberOfLabelsInFlood from '../../../helpers/fields-calculation/stamps/get-number-of-labels-in-flood'
import Edit from '../components/table/edit'
import renderDatetime from '../../../helpers/tables/render-datetime'
import Stamp from '../../../types/api/stamp'
import Delete from '../components/table/delete'
import Tube from '../../../types/api/tube'
import Reorder from '../components/table/reorder'
import { ColumnType } from 'antd/es/table'
import { EditableCellType } from './constants'
import User from '../../../types/api/user'
import StampImageLink from '../../../components/stamp-image-link/stamp-image-link'
import renderStampOrderUrgent from '../../../helpers/tables/render-stamp-order-urgent'
import { formatRollerDisplay } from '../../../helpers/format-roller-display'

interface ColumnGroupType<RecordType = {}> extends Omit<ColumnType<RecordType>, 'dataIndex'> {
    children: (
        | (ColumnType<RecordType> & { editable?: boolean; editableCellType?: EditableCellType })
        | ColumnGroupType
    )[]
}

export const columns: (
    | (ColumnType<any> & { editable?: boolean; editableCellType?: EditableCellType })
    | ColumnGroupType<any>
)[] = [
    {
        title: 'Тубус',
        fixed: 'left',
        children: [
            {
                dataIndex: 'number',
                title: 'Номер',
                width: 60,
                fixed: 'left',
            },
        ],
    },
    {
        title: 'Штамп',
        children: [
            {
                dataIndex: ['stamps', 0, 'number'],
                title: 'Номер',
                width: 108,
                render: (v: any, r: any) => {
                    const stamp = r.stamps?.[0]

                    return (
                        <Flex justify={'space-between'} align={'center'} gap={4}>
                            {v}
                            {stamp ? <StampImageLink stamp={stamp} download /> : null}
                        </Flex>
                    )
                },
            },
            {
                dataIndex: ['stamps', 0, 'width'],
                title: 'Ширина',
                width: 70,
            },
            {
                dataIndex: ['stamps', 0, 'length'],
                title: 'Длина',
                width: 60,
            },
            {
                dataIndex: ['stamps', 0, 'floods'],
                title: 'Ручьи',
                width: 60,
            },
            {
                dataIndex: ['stamps', 0, 'roller', 'roller'],
                title: 'Вал',
                width: 100,
                render: (v: number) => formatRollerDisplay(v),
            },
            {
                dataIndex: ['stamps', 0, 'range'],
                title: 'Расстояние',
                width: 100,
            },

            {
                dataIndex: ['stamps', 0, 'roller'],
                title: 'Зубья',
                width: 60,
                render: (v: any) => {
                    return v ? getTeeth(v) : undefined
                },
            },
            {
                dataIndex: ['stamps', 0],
                title: 'Макс. шир.',
                width: 86,
                render: (v: Stamp) => {
                    return v ? getMaxWidthOfMaterial(v) : undefined
                },
            },
            {
                dataIndex: 'stamps',
                title: (
                    <Tooltip placement='topRight' title={'Количество этикеток в одном ручье'}>
                        В 1 р.
                    </Tooltip>
                ),
                width: 70,
                render: (v: Stamp[]) => {
                    return getNumberOfLabelsInFlood({
                        roller: v[0]?.roller?.roller,
                        range: v[0]?.range,
                        length: v[0]?.length,
                    })
                },
            },
            {
                dataIndex: ['stamps', 0, 'radius'],
                title: 'Радиус',
                width: 65,
            },
            {
                dataIndex: ['stamps', 0, 'shape'],
                title: 'Форма',
                width: 130,
            },
            {
                dataIndex: ['stamps', 0],
                title: 'Материалы',
                width: 130,
                render(v: Stamp) {
                    const material_groups = v?.material_groups
                    const res = material_groups ? material_groups : [{ name: '' }]
                    return res.map(x => x.name).join(', ')
                },
            },
            {
                dataIndex: ['stamps', 0, 'contractor', 'name'],
                title: 'Производитель',
                width: 120,
            },
            {
                dataIndex: ['stamps', 0, 'externalId'],
                title: 'Внешний ID',
                width: 90,
            },
            {
                dataIndex: ['stamps', 0, 'quantity'],
                title: 'Кол-во',
                width: 60,
            },
            {
                dataIndex: ['stamps', 0, 'locked'],
                title: 'Блокировка',
                width: 90,
                render: (v: string) => {
                    return v ? 'Не использовать!' : ''
                },
            },

            {
                dataIndex: ['stamps', 0, 'comment'],
                title: 'Примечание',
                width: 100,
            },
        ],
    },
    {
        title: 'Закупка',
        editable: true,
        children: [
            {
                dataIndex: ['stamps', 0, 'last_order', 'initiator'],
                title: 'Менеджер',
                editable: true,
                editableCellType: 'selectUser',
                width: 200,
                render(value: User) {
                    return value ? value.username : ''
                },
            },
            {
                dataIndex: ['stamps', 0, 'last_order', 'createdAt'],
                title: 'Дата заказа',
                width: 120,
                render: (v: string) => {
                    return v ? renderDatetime(v) : undefined
                },
            },
            {
                dataIndex: ['stamps', 0, 'last_order', 'estimatedDate'],
                title: 'Дата поставки',
                width: 150,
                editable: true,
                editableCellType: 'datePicker',
                render: (v: string) => {
                    return v ? renderDatetime(v) : undefined
                },
            },
            {
                dataIndex: ['stamps', 0, 'last_order', 'endDate'],
                title: 'Факт. дата отгрузки',
                width: 140,
                render: (v: string) => {
                    return v ? renderDatetime(v) : undefined
                },
            },
            {
                dataIndex: ['stamps', 0, 'last_order', 'orderStatus'],
                title: 'Статус',
                width: 80,
                editable: true,
                editableCellType: 'select',
            },
            {
                title: 'Срочно',
                width: 70,
                align: 'center',
                render: (_: unknown, record: Tube) =>
                    renderStampOrderUrgent(record.stamps?.[0]?.last_order?.urgent),
            },
            {
                dataIndex: ['stamps', 0, 'last_order', 'price'],
                title: 'Цена',
                width: 100,
                editable: true,
                editableCellType: 'input',
            },
        ],
    },

    {
        title: 'Действия',
        fixed: 'right',
        width: 80,
        render: (v, record: Tube) => {
            const stamp = record.stamps ? record.stamps[0] : undefined

            return (
                <Space>
                    <Reorder tube={record} />
                    <Edit stampDocId={stamp?.documentId || 'new'} />
                    <Delete tube={record} />
                </Space>
            )
        },
    },
]
